import type { PlayerOptions } from './types.js';

type PlayFn = (opts: PlayerOptions) => Promise<unknown>;

/**
 * Scan the document for `[data-mpwa-video-ad]` elements and spawn a
 * player for each. Idempotent: marks each handled element with
 * `data-mpwa-video-init` so a second call is a no-op.
 */
export function initAuto(play: PlayFn): void {
  const els = document.querySelectorAll<HTMLElement>('[data-mpwa-video-ad]:not([data-mpwa-video-init])');
  els.forEach((el) => {
    el.setAttribute('data-mpwa-video-init', '1');
    const url = el.getAttribute('data-vast-url') ?? undefined;
    const xml = el.getAttribute('data-vast-xml') ?? undefined;
    if (!url && !xml) return;
    const skipAttr = el.getAttribute('data-skip-after');
    const skipAfter = skipAttr !== null ? parseInt(skipAttr, 10) : 5;
    const locale = (el.getAttribute('data-locale') as 'en' | 'ar') ?? 'en';
    const muted = el.getAttribute('data-muted') !== 'false';
    const autoplay = el.getAttribute('data-autoplay') !== 'false';
    const controls = el.getAttribute('data-controls') !== 'false';
    const companionId = el.getAttribute('data-companion');
    const companionContainer = companionId ? document.getElementById(companionId) : null;
    const outstream = el.getAttribute('data-outstream') === 'true';
    const thrAttr = el.getAttribute('data-viewability-threshold');
    const viewabilityThreshold = thrAttr !== null ? parseFloat(thrAttr) : undefined;
    const collapseAttr = el.getAttribute('data-collapse-on-complete');
    const collapseOnComplete = collapseAttr === null ? undefined : collapseAttr !== 'false';
    const opts: PlayerOptions = {
      container: el,
      skipAfter: Number.isFinite(skipAfter) ? skipAfter : 5,
      autoplay,
      muted,
      controls,
      locale,
    };
    if (url) opts.url = url;
    if (xml) opts.xml = xml;
    if (companionContainer) opts.companionContainer = companionContainer;
    if (outstream) opts.outstream = true;
    if (viewabilityThreshold !== undefined && Number.isFinite(viewabilityThreshold)) {
      opts.viewabilityThreshold = viewabilityThreshold;
    }
    if (collapseOnComplete !== undefined) opts.collapseOnComplete = collapseOnComplete;
    play(opts).catch(() => {
      // Best-effort — fail silently so a broken VAST doesn't break the page.
    });
  });
}
